'use client';

import { c as _c } from "react/compiler-runtime";
import { useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { Button } from '@module/mdrt-org/shared/components/button/button';
import { Dialog } from '@module/mdrt-org/shared/components/dialog/dialog';
import { FieldController } from '@module/mdrt-org/shared/components/field-controller/field-controller';
import { useUserContext } from '@module/mdrt-org/shared/components/providers/user-context';
import { SelectField } from '@module/mdrt-org/shared/components/select-field/select-field';
import { MAX_BIO_LENGTH, SOCIAL_MEDIA_LINK_LENGTH } from '@module/mdrt-org/shared/utils/constants/inputs-constants';
import { deleteProfilePicture } from '@module/mdrt-org/shared/utils/data/delete-profile-picture';
import { AccountSettingsFormStep } from '@module/mdrt-org/shared/utils/enums/account-settings-form-step';
import { AccountSettingsMutationKey } from '@module/mdrt-org/shared/utils/enums/account-settings-mutation-key';
import { allowedCharactersTransform } from '@module/mdrt-org/shared/utils/helpers/allowed-characters-transform';
import { getProfileDisplayName } from '@module/mdrt-org/shared/utils/helpers/get-profile-display-name';
import { useAddDefaultErrorToast } from '@module/mdrt-org/shared/utils/hooks/use-add-default-error-toast';
import { useConfirmNavigation } from '@module/mdrt-org/shared/utils/hooks/use-confirm-navigation';
import { useMutationHandler } from '@module/mdrt-org/shared/utils/hooks/use-mutation-handler';
import { useProfilePictureUploadMutation } from '@module/mdrt-org/shared/utils/hooks/use-profile-picture-upload-mutation';
import { ProfilePhotoContainer } from '@shared/profile-dashboard-sections/profile-dashboard-hero-section/components/profile-photo-container/profile-photo-container';
import { FormInput } from '@shared/ui-components/form-input/form-input';
import { FormTextarea } from '@shared/ui-components/form-textarea/form-textarea';
import { FormWrapper } from '@shared/ui-components/form-wrapper/form-wrapper';
import { ProfilePhotoSize } from '@shared/ui-components/profile-photo/profile-photo';
import { formBaseConfig } from '@shared/utils/constants/formBaseConfig';
import { GTM_ACTION_CLICK, GTM_EVENT_ACCOUNT_PROFILE_UPDATE, GTM_EVENT_UPLOAD_PHOTO, GTM_EVENT_USER_INTERACTIONS } from '@shared/utils/constants/gtm';
import { ButtonType } from '@shared/utils/enums/button-type';
import { getTimezoneOptions } from '@shared/utils/helpers/get-timezone-options';
import { sendGtmEvent } from '@shared/utils/helpers/send-gtm-event';
import { SocialPlatform, validateSocialLink } from '@shared/utils/helpers/social-link-validators';
import { type SelectOption } from '@shared/utils/types/select-option';
import { type ServerActionResponse } from '@shared/utils/types/server-action';
import { saveAccountSettingsProfileStepAction } from '../../actions/save-account-settings-profile-step-action';
import { useAccountSettingsPageContext } from '../../providers/account-settings-page-context';
import { type ProfileFormData } from '../../utils/types/form-data';
import { type AccountSettingsProfileContent } from './get-account-settings-profile-content';
import { MembershipPrintouts } from './membership-printouts/membership-printouts';
import styles from './profile-form.module.scss';
type ProfileFormProps = {
  cmsContent: AccountSettingsProfileContent;
  isPrintoutsVisible: boolean;
  membershipCertificateEndpointUrl: string;
  tabUrl: string;
};
const transformInputLineBreaks = (inputString: null | string) => inputString && inputString.replaceAll('\r\n', '\n');
export const ProfileForm = t0 => {
  const $ = _c(141);
  const {
    cmsContent,
    isPrintoutsVisible,
    membershipCertificateEndpointUrl,
    tabUrl
  } = t0;
  const {
    countriesData,
    customerProfileContextData,
    profilePictureContextData,
    resetCustomerProfileContextData,
    resetProfilePictureContextData,
    setCustomerProfileContextData
  } = useAccountSettingsPageContext();
  const {
    personId
  } = useUserContext();
  const addErrorToast = useAddDefaultErrorToast();
  const {
    cancel: abortOngoingRequests,
    mutate: uploadProfilePicture
  } = useProfilePictureUploadMutation();
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = getTimezoneOptions();
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const timezoneOptions = t1;
  let t2;
  if ($[1] !== customerProfileContextData) {
    t2 = getProfileDisplayName(customerProfileContextData);
    $[1] = customerProfileContextData;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const profileDisplayName = t2;
  let t3;
  if ($[3] !== countriesData || $[4] !== customerProfileContextData.countryId) {
    let t4;
    if ($[6] !== customerProfileContextData.countryId) {
      t4 = country => country.countryId === customerProfileContextData.countryId;
      $[6] = customerProfileContextData.countryId;
      $[7] = t4;
    } else {
      t4 = $[7];
    }
    t3 = countriesData.find(t4);
    $[3] = countriesData;
    $[4] = customerProfileContextData.countryId;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const defaultCountry = t3;
  let t4;
  if ($[8] !== uploadProfilePicture) {
    t4 = async formData => {
      if (formData instanceof FormData) {
        const file = formData.get("file");
        if (file && file instanceof File) {
          sendGtmEvent({
            action: GTM_ACTION_CLICK,
            event: GTM_EVENT_USER_INTERACTIONS,
            eventName: GTM_EVENT_UPLOAD_PHOTO,
            label: file?.name || ""
          });
        }
      }
      return await uploadProfilePicture(formData);
    };
    $[8] = uploadProfilePicture;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  const handleSubmitProfilePicture = t4;
  let t5;
  if ($[10] !== customerProfileContextData.bio) {
    t5 = transformInputLineBreaks(customerProfileContextData.bio) || "";
    $[10] = customerProfileContextData.bio;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  let t6;
  if ($[12] !== defaultCountry) {
    t6 = defaultCountry ? {
      label: defaultCountry.name,
      value: defaultCountry.countryId
    } : undefined;
    $[12] = defaultCountry;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  const t7 = profileDisplayName || "";
  const t8 = customerProfileContextData.facebook || "";
  const t9 = customerProfileContextData.instagram || "";
  const t10 = customerProfileContextData.linkedin || "";
  let t11;
  if ($[14] !== customerProfileContextData.timeZoneId) {
    t11 = timezoneOptions?.find(timezoneOption => timezoneOption?.value === customerProfileContextData?.timeZoneId);
    $[14] = customerProfileContextData.timeZoneId;
    $[15] = t11;
  } else {
    t11 = $[15];
  }
  const t12 = customerProfileContextData.twitter || "";
  let t13;
  if ($[16] !== t10 || $[17] !== t11 || $[18] !== t12 || $[19] !== t5 || $[20] !== t6 || $[21] !== t7 || $[22] !== t8 || $[23] !== t9) {
    t13 = {
      bio: t5,
      country: t6,
      displayName: t7,
      facebook: t8,
      instagram: t9,
      linkedin: t10,
      timeZoneId: t11,
      twitter: t12
    };
    $[16] = t10;
    $[17] = t11;
    $[18] = t12;
    $[19] = t5;
    $[20] = t6;
    $[21] = t7;
    $[22] = t8;
    $[23] = t9;
    $[24] = t13;
  } else {
    t13 = $[24];
  }
  let t14;
  if ($[25] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = {
      keepValues: true
    };
    $[25] = t14;
  } else {
    t14 = $[25];
  }
  let t15;
  if ($[26] !== t13) {
    t15 = {
      ...formBaseConfig,
      defaultValues: t13,
      resetOptions: t14
    };
    $[26] = t13;
    $[27] = t15;
  } else {
    t15 = $[27];
  }
  const formMethods = useForm(t15);
  const {
    formState: t16,
    reset
  } = formMethods;
  const {
    isDirty
  } = t16;
  let t17;
  if ($[28] !== countriesData) {
    t17 = countriesData?.map(_temp);
    $[28] = countriesData;
    $[29] = t17;
  } else {
    t17 = $[29];
  }
  const countriesOptions = t17;
  let t18;
  if ($[30] !== addErrorToast || $[31] !== resetCustomerProfileContextData || $[32] !== resetProfilePictureContextData) {
    t18 = result => {
      if (result.status !== 200 && "message" in result) {
        resetCustomerProfileContextData();
        resetProfilePictureContextData();
        addErrorToast();
      }
    };
    $[30] = addErrorToast;
    $[31] = resetCustomerProfileContextData;
    $[32] = resetProfilePictureContextData;
    $[33] = t18;
  } else {
    t18 = $[33];
  }
  const onFailure = t18;
  let t19;
  if ($[34] === Symbol.for("react.memo_cache_sentinel")) {
    t19 = [AccountSettingsMutationKey.PATCH_ACCOUNT_SETTINGS];
    $[34] = t19;
  } else {
    t19 = $[34];
  }
  let t20;
  if ($[35] !== formMethods || $[36] !== reset) {
    t20 = () => reset({
      ...formMethods.getValues()
    });
    $[35] = formMethods;
    $[36] = reset;
    $[37] = t20;
  } else {
    t20 = $[37];
  }
  let t21;
  if ($[38] !== onFailure || $[39] !== t20) {
    t21 = {
      mutationFunction: saveAccountSettingsProfileStepAction,
      mutationKey: t19,
      onFailure,
      onSuccessHandler: t20
    };
    $[38] = onFailure;
    $[39] = t20;
    $[40] = t21;
  } else {
    t21 = $[40];
  }
  const {
    isMutationPending,
    mutate
  } = useMutationHandler(t21);
  let t22;
  if ($[41] !== customerProfileContextData || $[42] !== mutate || $[43] !== personId || $[44] !== setCustomerProfileContextData) {
    t22 = async data => {
      const formData_0 = new FormData();
      setCustomerProfileContextData({
        ...customerProfileContextData,
        bio: data.bio || "",
        countryId: Number(data.country?.value) || null,
        facebook: data.facebook || "",
        instagram: data.instagram || "",
        linkedin: data.linkedin || "",
        timeZoneId: data.timeZoneId?.value?.toString() || "",
        twitter: data.twitter || ""
      });
      formData_0.append("accountSettingStep", AccountSettingsFormStep.Profile);
      formData_0.append("profileData['bio']", data.bio || "");
      formData_0.append("profileData['countryId']", data.country ? data.country.value.toString() : "");
      formData_0.append("profileData['timeZoneId']", data.timeZoneId?.value?.toString() || "");
      formData_0.append("profileData['linkedin']", data.linkedin || "");
      formData_0.append("profileData['facebook']", data.facebook || "");
      formData_0.append("profileData['instagram']", data.instagram || "");
      formData_0.append("profileData['twitter']", data.twitter || "");
      mutate({
        formData: formData_0,
        personId
      });
      sendGtmEvent({
        action: GTM_ACTION_CLICK,
        event: GTM_EVENT_USER_INTERACTIONS,
        eventName: GTM_EVENT_ACCOUNT_PROFILE_UPDATE,
        label: "Profile"
      });
    };
    $[41] = customerProfileContextData;
    $[42] = mutate;
    $[43] = personId;
    $[44] = setCustomerProfileContextData;
    $[45] = t22;
  } else {
    t22 = $[45];
  }
  const handleSubmit = t22;
  useConfirmNavigation(isDirty, tabUrl);
  let t23;
  if ($[46] !== cmsContent.tabTitle) {
    t23 = <h1 className={styles["profile-form__heading"]}>{cmsContent.tabTitle}</h1>;
    $[46] = cmsContent.tabTitle;
    $[47] = t23;
  } else {
    t23 = $[47];
  }
  let t24;
  if ($[48] !== personId) {
    t24 = async () => await deleteProfilePicture({
      personId
    });
    $[48] = personId;
    $[49] = t24;
  } else {
    t24 = $[49];
  }
  const t25 = profilePictureContextData ?? null;
  let t26;
  if ($[50] !== abortOngoingRequests || $[51] !== cmsContent.profilePicture || $[52] !== handleSubmitProfilePicture || $[53] !== t24 || $[54] !== t25) {
    t26 = <ProfilePhotoContainer abortOngoingRequests={abortOngoingRequests} cmsContent={cmsContent.profilePicture} dialogComponent={Dialog} handleProfilePictureDelete={t24} handleSubmitProfilePicture={handleSubmitProfilePicture} initialProfilePicture={t25} size={ProfilePhotoSize.XSmall} />;
    $[50] = abortOngoingRequests;
    $[51] = cmsContent.profilePicture;
    $[52] = handleSubmitProfilePicture;
    $[53] = t24;
    $[54] = t25;
    $[55] = t26;
  } else {
    t26 = $[55];
  }
  let t27;
  if ($[56] === Symbol.for("react.memo_cache_sentinel")) {
    t27 = <FormInput disabled={true} />;
    $[56] = t27;
  } else {
    t27 = $[56];
  }
  let t28;
  if ($[57] !== cmsContent.displayName) {
    t28 = <FieldController label={cmsContent.displayName} name="displayName">{t27}</FieldController>;
    $[57] = cmsContent.displayName;
    $[58] = t28;
  } else {
    t28 = $[58];
  }
  let t29;
  if ($[59] !== cmsContent.requiredLabel) {
    t29 = {
      required: cmsContent.requiredLabel
    };
    $[59] = cmsContent.requiredLabel;
    $[60] = t29;
  } else {
    t29 = $[60];
  }
  let t30;
  if ($[61] !== cmsContent.country || $[62] !== countriesOptions || $[63] !== t29) {
    t30 = <SelectField id="country" isSearchable={true} label={cmsContent.country} name="country" options={countriesOptions} rules={t29} />;
    $[61] = cmsContent.country;
    $[62] = countriesOptions;
    $[63] = t29;
    $[64] = t30;
  } else {
    t30 = $[64];
  }
  let t31;
  if ($[65] !== cmsContent.requiredLabel) {
    t31 = {
      required: cmsContent.requiredLabel
    };
    $[65] = cmsContent.requiredLabel;
    $[66] = t31;
  } else {
    t31 = $[66];
  }
  let t32;
  if ($[67] !== cmsContent.timeZone || $[68] !== t31) {
    t32 = <SelectField id="timeZoneId" isSearchable={true} label={cmsContent.timeZone} name="timeZoneId" options={timezoneOptions} rules={t31} />;
    $[67] = cmsContent.timeZone;
    $[68] = t31;
    $[69] = t32;
  } else {
    t32 = $[69];
  }
  let t33;
  if ($[70] === Symbol.for("react.memo_cache_sentinel")) {
    t33 = <FormTextarea />;
    $[70] = t33;
  } else {
    t33 = $[70];
  }
  let t34;
  if ($[71] !== cmsContent.bio) {
    t34 = <FieldController displayMaxLengthCounter={true} label={cmsContent.bio} maxLength={MAX_BIO_LENGTH} name="bio" transform={allowedCharactersTransform}>{t33}</FieldController>;
    $[71] = cmsContent.bio;
    $[72] = t34;
  } else {
    t34 = $[72];
  }
  let t35;
  if ($[73] !== cmsContent || $[74] !== customerProfileContextData || $[75] !== isPrintoutsVisible || $[76] !== membershipCertificateEndpointUrl) {
    t35 = <MembershipPrintouts cmsContent={cmsContent} customerProfile={customerProfileContextData} isPrintoutsVisible={isPrintoutsVisible} membershipCertificateEndpointUrl={membershipCertificateEndpointUrl} />;
    $[73] = cmsContent;
    $[74] = customerProfileContextData;
    $[75] = isPrintoutsVisible;
    $[76] = membershipCertificateEndpointUrl;
    $[77] = t35;
  } else {
    t35 = $[77];
  }
  let t36;
  if ($[78] === Symbol.for("react.memo_cache_sentinel")) {
    t36 = <hr className={styles["profile-form__separator"]} />;
    $[78] = t36;
  } else {
    t36 = $[78];
  }
  let t37;
  if ($[79] !== cmsContent.socialLinks.title) {
    t37 = <h2 className={styles["profile-form__heading"]}>{cmsContent.socialLinks.title}</h2>;
    $[79] = cmsContent.socialLinks.title;
    $[80] = t37;
  } else {
    t37 = $[80];
  }
  let t38;
  if ($[81] !== cmsContent.socialLinks.shareSocialLinks) {
    t38 = <p className={styles["profile-form__sub-heading"]}>{cmsContent.socialLinks.shareSocialLinks}</p>;
    $[81] = cmsContent.socialLinks.shareSocialLinks;
    $[82] = t38;
  } else {
    t38 = $[82];
  }
  const t39 = cmsContent.socialLinks.linkedIn;
  let t40;
  if ($[83] !== cmsContent.socialLinks.invalidLinkMessage) {
    t40 = validateSocialLink(SocialPlatform.LinkedIn, cmsContent.socialLinks.invalidLinkMessage);
    $[83] = cmsContent.socialLinks.invalidLinkMessage;
    $[84] = t40;
  } else {
    t40 = $[84];
  }
  let t41;
  if ($[85] !== t40) {
    t41 = {
      validate: t40
    };
    $[85] = t40;
    $[86] = t41;
  } else {
    t41 = $[86];
  }
  let t42;
  if ($[87] === Symbol.for("react.memo_cache_sentinel")) {
    t42 = <FormInput />;
    $[87] = t42;
  } else {
    t42 = $[87];
  }
  let t43;
  if ($[88] !== cmsContent.socialLinks.linkedIn || $[89] !== t41) {
    t43 = <FieldController label={t39} maxLength={SOCIAL_MEDIA_LINK_LENGTH} name="linkedin" rules={t41}>{t42}</FieldController>;
    $[88] = cmsContent.socialLinks.linkedIn;
    $[89] = t41;
    $[90] = t43;
  } else {
    t43 = $[90];
  }
  const t44 = cmsContent.socialLinks.facebook;
  let t45;
  if ($[91] !== cmsContent.socialLinks.invalidLinkMessage) {
    t45 = validateSocialLink(SocialPlatform.Facebook, cmsContent.socialLinks.invalidLinkMessage);
    $[91] = cmsContent.socialLinks.invalidLinkMessage;
    $[92] = t45;
  } else {
    t45 = $[92];
  }
  let t46;
  if ($[93] !== t45) {
    t46 = {
      validate: t45
    };
    $[93] = t45;
    $[94] = t46;
  } else {
    t46 = $[94];
  }
  let t47;
  if ($[95] === Symbol.for("react.memo_cache_sentinel")) {
    t47 = <FormInput />;
    $[95] = t47;
  } else {
    t47 = $[95];
  }
  let t48;
  if ($[96] !== cmsContent.socialLinks.facebook || $[97] !== t46) {
    t48 = <FieldController label={t44} maxLength={SOCIAL_MEDIA_LINK_LENGTH} name="facebook" rules={t46}>{t47}</FieldController>;
    $[96] = cmsContent.socialLinks.facebook;
    $[97] = t46;
    $[98] = t48;
  } else {
    t48 = $[98];
  }
  const t49 = cmsContent.socialLinks.instagram;
  let t50;
  if ($[99] !== cmsContent.socialLinks.invalidLinkMessage) {
    t50 = validateSocialLink(SocialPlatform.Instagram, cmsContent.socialLinks.invalidLinkMessage);
    $[99] = cmsContent.socialLinks.invalidLinkMessage;
    $[100] = t50;
  } else {
    t50 = $[100];
  }
  let t51;
  if ($[101] !== t50) {
    t51 = {
      validate: t50
    };
    $[101] = t50;
    $[102] = t51;
  } else {
    t51 = $[102];
  }
  let t52;
  if ($[103] === Symbol.for("react.memo_cache_sentinel")) {
    t52 = <FormInput />;
    $[103] = t52;
  } else {
    t52 = $[103];
  }
  let t53;
  if ($[104] !== cmsContent.socialLinks.instagram || $[105] !== t51) {
    t53 = <FieldController label={t49} maxLength={SOCIAL_MEDIA_LINK_LENGTH} name="instagram" rules={t51}>{t52}</FieldController>;
    $[104] = cmsContent.socialLinks.instagram;
    $[105] = t51;
    $[106] = t53;
  } else {
    t53 = $[106];
  }
  const t54 = cmsContent.socialLinks.twitter;
  let t55;
  if ($[107] !== cmsContent.socialLinks.invalidLinkMessage) {
    t55 = validateSocialLink(SocialPlatform.Twitter, cmsContent.socialLinks.invalidLinkMessage);
    $[107] = cmsContent.socialLinks.invalidLinkMessage;
    $[108] = t55;
  } else {
    t55 = $[108];
  }
  let t56;
  if ($[109] !== t55) {
    t56 = {
      validate: t55
    };
    $[109] = t55;
    $[110] = t56;
  } else {
    t56 = $[110];
  }
  let t57;
  if ($[111] === Symbol.for("react.memo_cache_sentinel")) {
    t57 = <FormInput />;
    $[111] = t57;
  } else {
    t57 = $[111];
  }
  let t58;
  if ($[112] !== cmsContent.socialLinks.twitter || $[113] !== t56) {
    t58 = <FieldController label={t54} maxLength={SOCIAL_MEDIA_LINK_LENGTH} name="twitter" rules={t56}>{t57}</FieldController>;
    $[112] = cmsContent.socialLinks.twitter;
    $[113] = t56;
    $[114] = t58;
  } else {
    t58 = $[114];
  }
  let t59;
  if ($[115] !== t43 || $[116] !== t48 || $[117] !== t53 || $[118] !== t58) {
    t59 = <div className={styles["profile-form__social-links-fields"]}>{t43}{t48}{t53}{t58}</div>;
    $[115] = t43;
    $[116] = t48;
    $[117] = t53;
    $[118] = t58;
    $[119] = t59;
  } else {
    t59 = $[119];
  }
  let t60;
  if ($[120] !== t37 || $[121] !== t38 || $[122] !== t59) {
    t60 = <div>{t37}{t38}{t59}</div>;
    $[120] = t37;
    $[121] = t38;
    $[122] = t59;
    $[123] = t60;
  } else {
    t60 = $[123];
  }
  const t61 = !isDirty || isMutationPending;
  let t62;
  if ($[124] !== cmsContent.saveChanges || $[125] !== t61) {
    t62 = <div className={styles["profile-form__footer"]}><Button disabled={t61} type={ButtonType.SUBMIT}>{cmsContent.saveChanges}</Button></div>;
    $[124] = cmsContent.saveChanges;
    $[125] = t61;
    $[126] = t62;
  } else {
    t62 = $[126];
  }
  let t63;
  if ($[127] !== formMethods || $[128] !== handleSubmit || $[129] !== t28 || $[130] !== t30 || $[131] !== t32 || $[132] !== t34 || $[133] !== t35 || $[134] !== t60 || $[135] !== t62) {
    t63 = <FormWrapper className={styles["profile-form"]} dataTestId="profile-form" formMethods={formMethods} onSubmit={handleSubmit}>{t28}{t30}{t32}{t34}{t35}{t36}{t60}{t62}</FormWrapper>;
    $[127] = formMethods;
    $[128] = handleSubmit;
    $[129] = t28;
    $[130] = t30;
    $[131] = t32;
    $[132] = t34;
    $[133] = t35;
    $[134] = t60;
    $[135] = t62;
    $[136] = t63;
  } else {
    t63 = $[136];
  }
  let t64;
  if ($[137] !== t23 || $[138] !== t26 || $[139] !== t63) {
    t64 = <div className={styles["profile-form"]}>{t23}{t26}{t63}</div>;
    $[137] = t23;
    $[138] = t26;
    $[139] = t63;
    $[140] = t64;
  } else {
    t64 = $[140];
  }
  return t64;
};
function _temp(country_0) {
  return {
    label: country_0.name,
    value: `${country_0.countryId}`
  };
}